import Box from "@mui/joy/Box";
import List from "@mui/joy/List";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, ListDivider, Stack } from "@mui/joy";
import { useRecoilState, useRecoilValue } from "recoil";
// Note: react-window is installed but not used yet - will be added in future if needed for 50+ items
import {
  AutomaticThought,
  Beliefs,
  CONCEPT_DOMAIN,
  CONCEPT_NOTE_AUTOMATIC_THOUGHT_LIST_ENDPOINT,
  CONCEPT_NOTE_BELIEFS_DETAIL_ENDPOINT,
  CONCEPT_NOTE_CONCERN_AND_GOAL_DETAIL_ENDPOINT,
  conceptApi,
  ConcernAndGoal,
} from "api/conceptApi";
import { useQueries } from "@tanstack/react-query";
import Memo from "components/coachRightSidebar/conceptNote/Memo";
import { addAlpha, extractHexColor } from "styles/hexOpacity";
import { theme } from "styles/theme";
import AutomaticThoughtListItem from "./automaticThought/AutomaticThoughtListItem";
import ConcernAndGoalItem from "./concernAndGoal/ConcernAndGoalItem";
import CoreBeliefsItem from "./beliefs/CoreBeliefsItem";
import IntermediateBeliefsItem from "./beliefs/IntermediateBeliefsItem";
import CoreExperienceItem from "./beliefs/CoreExperienceItem";
import { selectedThoughtRecordKeyAtom } from "../../../recoil/selectedThoughtRecordAtom";
import { selectedAutomaticThoughtIdAtom } from "../../../recoil/selectedAutomaticThoughtIdAtom";
import { selectedConceptNoteSectionAtom } from "../../../recoil/selectedConceptNoteSectionAtom";
import AutomaticThoughtForValueListItem from "./automaticThought/AutomaticThoughtForValueListItem";

export default function ConceptNoteV2({
  patientId,
  isActive,
}: {
  patientId: number;
  isActive: boolean;
}) {
  const [selectedThoughtRecordKey, setSelectedThoughtRecordKey] =
    useRecoilState(selectedThoughtRecordKeyAtom);
  const selectedAutomaticThoughtId = useRecoilValue(
    selectedAutomaticThoughtIdAtom
  );
  const selectedConceptNoteSection = useRecoilValue(
    selectedConceptNoteSectionAtom
  );

  const [automaticThoughtList, setAutomaticThoughtList] = useState<
    AutomaticThought[]
  >([]);

  const [concernAndGoal, setConcernAndGoal] = useState<
    ConcernAndGoal | undefined
  >(undefined);

  type automaticThoughtType = "함정 찾기" | "가치 찾기";
  const [currentAutomaticThoughtType, setCurrentAutomaticThoughtType] =
    useState<automaticThoughtType>("함정 찾기");

  const containerRef = useRef<HTMLDivElement>(null);

  const [beliefs, setBeliefs] = useState<Beliefs | undefined>(undefined);

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Track if initial scroll has been performed
  const hasInitialScrolled = useRef(false);

  // Refs for belief sections
  const concernAndGoalRef = useRef<HTMLDivElement>(null);
  const coreExperienceRef = useRef<HTMLDivElement>(null);
  const coreBeliefsRef = useRef<HTMLDivElement>(null);
  const intermediateBeliefsRef = useRef<HTMLDivElement>(null);

  // Memoize filtered lists to avoid re-filtering on every render
  const trapThoughts = useMemo(
    () => automaticThoughtList.filter((e) => !e.isForValue),
    [automaticThoughtList]
  );

  const valueThoughts = useMemo(
    () => automaticThoughtList.filter((e) => e.isForValue),
    [automaticThoughtList]
  );

  // (1) selectedThoughtRecordKey가 바뀌면, 해당 항목이 함정인지 가치인지 판별해 탭 전환
  useEffect(() => {
    if (!selectedThoughtRecordKey) return;

    const found = automaticThoughtList.find(
      (item) => item.thoughtRecordKey === selectedThoughtRecordKey
    );

    if (found) {
      setCurrentAutomaticThoughtType(
        found.isForValue ? "가치 찾기" : "함정 찾기"
      );
    }
  }, [selectedThoughtRecordKey, automaticThoughtList]);

  useEffect(() => {
    if (!selectedAutomaticThoughtId) {
      return;
    }

    const found = automaticThoughtList.find(
      (item) =>
        item.sequence === selectedAutomaticThoughtId?.id &&
        item.isForValue === !!selectedAutomaticThoughtId?.isForValue
    );

    if (found) {
      setCurrentAutomaticThoughtType(
        found.isForValue ? "가치 찾기" : "함정 찾기"
      );
      setSelectedThoughtRecordKey(found.thoughtRecordKey);
    }
  }, [
    selectedAutomaticThoughtId,
    automaticThoughtList,
    setSelectedThoughtRecordKey,
  ]);

  // Scroll logic with debouncing - includes automaticThoughtList to handle newly added items
  useEffect(() => {
    if (!isActive || !selectedThoughtRecordKey) return;

    const timeoutId = setTimeout(() => {
      const targetEl = itemRefs.current[selectedThoughtRecordKey];
      if (targetEl && containerRef.current) {
        const headerHeight = 80;
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const elementTop = targetEl.getBoundingClientRect().top;
        const offset =
          elementTop -
          containerTop +
          containerRef.current.scrollTop -
          headerHeight;

        containerRef.current.scrollTo({
          top: offset,
          behavior: "smooth",
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isActive, selectedThoughtRecordKey, currentAutomaticThoughtType, automaticThoughtList]);

  // Belief section focusing: scroll and temporary highlight
  useEffect(() => {
    if (!isActive || !selectedConceptNoteSection) return;

    const targetRef =
      selectedConceptNoteSection === "concernAndGoal"
        ? concernAndGoalRef
        : selectedConceptNoteSection === "experience"
          ? coreExperienceRef
          : selectedConceptNoteSection === "core"
            ? coreBeliefsRef
            : intermediateBeliefsRef;

    const timeoutId = setTimeout(() => {
      const targetEl = targetRef.current;
      if (targetEl && containerRef.current) {
        const headerHeight = 80;
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const elementTop = targetEl.getBoundingClientRect().top;
        const offset =
          elementTop -
          containerTop +
          containerRef.current.scrollTop -
          headerHeight;

        containerRef.current.scrollTo({ top: offset, behavior: "smooth" });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isActive, selectedConceptNoteSection, beliefs]);

  // 초기화
  useEffect(() => {
    setCurrentAutomaticThoughtType("함정 찾기");
    setAutomaticThoughtList([]);
    hasInitialScrolled.current = false; // 환자 변경 시 초기 스크롤 플래그 초기화
  }, [patientId]);

  const concernAndGoalQueryFn = useCallback(
    () => conceptApi.noteConcernAndGoalDetail(patientId),
    [patientId]
  );

  const beliefsQueryFn = useCallback(
    () => conceptApi.noteBeliefsDetail(patientId),
    [patientId]
  );

  const automaticThoughtQueryFn = useCallback(
    () => conceptApi.noteAutomaticThoughtList(patientId),
    [patientId]
  );

  // 여러 쿼리를 parallel 하게
  const results = useQueries({
    queries: [
      {
        queryKey: [
          CONCEPT_DOMAIN,
          CONCEPT_NOTE_CONCERN_AND_GOAL_DETAIL_ENDPOINT,
          patientId,
        ],
        queryFn: concernAndGoalQueryFn,
      },
      {
        queryKey: [
          CONCEPT_DOMAIN,
          CONCEPT_NOTE_BELIEFS_DETAIL_ENDPOINT,
          patientId,
        ],
        queryFn: beliefsQueryFn,
      },
      {
        queryKey: [
          CONCEPT_DOMAIN,
          CONCEPT_NOTE_AUTOMATIC_THOUGHT_LIST_ENDPOINT,
          patientId,
        ],
        queryFn: automaticThoughtQueryFn,
      },
    ],
  });

  const concernAndGoalResult = results[0].data?.result;
  const beliefsResult = results[1].data?.result;
  const automaticThoughtResult = results[2].data?.result;

  // 자동적 사고 목록 불러오기
  useEffect(() => {
    if (automaticThoughtResult) {
      setAutomaticThoughtList(automaticThoughtResult);

      // 초기 로드 시 첫 번째 열린 자동적 사고로 스크롤 (highlight 없이)
      if (!hasInitialScrolled.current && isActive) {
        const firstOpenThought = automaticThoughtResult.find(
          (thought) => thought.taskProgressStatus !== "completed"
        );

        if (firstOpenThought) {
          setTimeout(() => {
            const targetEl = itemRefs.current[firstOpenThought.thoughtRecordKey];
            if (targetEl && containerRef.current) {
              const headerHeight = 80;
              const containerTop = containerRef.current.getBoundingClientRect().top;
              const elementTop = targetEl.getBoundingClientRect().top;
              const offset =
                elementTop -
                containerTop +
                containerRef.current.scrollTop -
                headerHeight;

              containerRef.current.scrollTo({
                top: offset,
                behavior: "auto",
              });
            }
            hasInitialScrolled.current = true;
          }, 200);
        }
      }
    }
  }, [patientId, automaticThoughtResult, isActive]);

  useEffect(() => {
    if (concernAndGoalResult) {
      setConcernAndGoal(concernAndGoalResult);
    }
  }, [patientId, concernAndGoalResult]);

  useEffect(() => {
    if (beliefsResult) {
      setBeliefs(beliefsResult);
    }
  }, [patientId, beliefsResult]);

  // 로딩 중이면 스피너
  const isLoading = results.some((r) => r.isLoading);

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: "fixed", lg: "sticky" },
        transform: "translateX(0)",
        transition: "transform 0.4s, width 0.4s",
        width: "var(--InsideC-width)",
        height: {
          xs: "calc(100% - var(--Header-height, 0))",
          lg: "100%",
        },
        zIndex: { xs: 1000, lg: 0 },
        top: {
          xs: "var(--Header-height, 0)",
          lg: 0,
        },
        right: 0,
        pb: 0,
        flexShrink: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: "background.level2",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          minHeight: 0,
          overflow: "auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          px: "16px",
          pb: 5,
        }}
      >
        <List sx={{ pt: "20px" }}>
          {/* 메모 컴포넌트 */}
          <Memo />
          <ListDivider sx={{ my: "20px", backgroundColor: "divider" }} />

          {/* 고민 */}
          <Stack
            key={"concernAndGoal"}
            ref={concernAndGoalRef}
            sx={{
              pb: "20px",
              borderBottomWidth: "1px",
              borderColor: "divider",
              mb: "20px",
            }}
          >
            {concernAndGoal !== undefined ? (
              <ConcernAndGoalItem
                concernAndGoal={concernAndGoal}
                setConcernAndGoal={setConcernAndGoal}
              />
            ) : (
              <CircularProgress color="neutral" />
            )}
          </Stack>

          {/*믿음*/}
          {beliefs !== undefined ? (
            <>
              <Stack
                key={"coreExperience"}
                ref={coreExperienceRef}
                sx={{
                  pb: "20px",
                  borderBottomWidth: "1px",
                  borderColor: "divider",
                  mb: "20px",
                  transition: "background-color 0.3s",
                }}
              >
                <CoreExperienceItem beliefs={beliefs} setBeliefs={setBeliefs} />
              </Stack>

              <Stack
                key={"coreBeliefs"}
                ref={coreBeliefsRef}
                sx={{
                  pb: "20px",
                  borderBottomWidth: "1px",
                  borderColor: "divider",
                  mb: "20px",
                  transition: "background-color 0.3s",
                }}
              >
                <CoreBeliefsItem beliefs={beliefs} setBeliefs={setBeliefs} />
              </Stack>

              <Stack
                key={"intermediateBeliefs"}
                ref={intermediateBeliefsRef}
                sx={{
                  pb: "20px",
                  borderBottomWidth: "1px",
                  borderColor: "divider",
                  mb: "20px",
                  transition: "background-color 0.3s",
                }}
              >
                <IntermediateBeliefsItem
                  beliefs={beliefs}
                  setBeliefs={setBeliefs}
                />
              </Stack>
            </>
          ) : (
            <CircularProgress color="neutral" />
          )}

          {/* 자동적 사고 탭 */}
          <Stack direction="row" sx={{ width: "100%", mb: "20px" }}>
            {["함정 찾기", "가치 찾기"].map((tabLabel) => (
              <Box
                key={tabLabel}
                sx={{
                  pb: "8px",
                  flex: 1,
                  cursor: "pointer",
                  borderBottomWidth: "2px",
                  borderBottomColor:
                    currentAutomaticThoughtType === tabLabel
                      ? "primary.solid"
                      : addAlpha(
                          extractHexColor(theme.vars.palette.primary.soft),
                          0.1
                        ),
                }}
                onClick={() =>
                  setCurrentAutomaticThoughtType(
                    tabLabel as automaticThoughtType
                  )
                }
              >
                <Typography variant={"Footnote"} sx={{ textAlign: "center", fontWeight: 500 }}>
                  {tabLabel}
                </Typography>
              </Box>
            ))}
          </Stack>

          {currentAutomaticThoughtType === "함정 찾기" && (
            <>
              {trapThoughts.length > 0 ? (
                trapThoughts.map((thought, index) => (
                  <div
                    key={thought.automaticThoughtId}
                    ref={(el) => {
                      itemRefs.current[thought.thoughtRecordKey] = el;
                    }}
                  >
                    <AutomaticThoughtListItem
                      automaticThought={thought}
                      automaticThoughtList={automaticThoughtList}
                      onDelete={(id) =>
                        setAutomaticThoughtList((prev) =>
                          prev.filter((at) => at.automaticThoughtId !== id)
                        )
                      }
                      highlight={
                        thought.thoughtRecordKey === selectedThoughtRecordKey
                      }
                    />
                    {index < trapThoughts.length - 1 && (
                      <ListDivider
                        sx={{ my: "20px", backgroundColor: "divider" }}
                      />
                    )}
                  </div>
                ))
              ) : (
                <Typography variant={"Footnote"} sx={{ textAlign: "center" }}>
                  부정 기록에서 추가하세요
                </Typography>
              )}
            </>
          )}

          {currentAutomaticThoughtType === "가치 찾기" && (
            <>
              {valueThoughts.length > 0 ? (
                valueThoughts.map((thought, index) => (
                  <div
                    key={thought.automaticThoughtId}
                    ref={(el) => {
                      itemRefs.current[thought.thoughtRecordKey] = el;
                    }}
                  >
                    <AutomaticThoughtForValueListItem
                      automaticThought={thought}
                      totalCount={valueThoughts.length}
                      onDelete={(id) =>
                        setAutomaticThoughtList((prev) =>
                          prev.filter((at) => at.automaticThoughtId !== id)
                        )
                      }
                      highlight={
                        thought.thoughtRecordKey === selectedThoughtRecordKey
                      }
                    />
                    {index < valueThoughts.length - 1 && (
                      <ListDivider
                        sx={{ my: "20px", backgroundColor: "divider" }}
                      />
                    )}
                  </div>
                ))
              ) : (
                <Typography variant={"Footnote"} sx={{ textAlign: "center" }}>
                  긍정 기록에서 추가하세요
                </Typography>
              )}
            </>
          )}

          <ListDivider sx={{ my: "20px", backgroundColor: "divider" }} />
        </List>
      </Box>
    </Sheet>
  );
}
